import { Button, Form } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import BackButtonIcon from "../../../../assets/icons/BackButtonIcon";
import ActionModal from "../../../../components/ui/ActionModal";
import {
  useCreateTemplateMutation,
  useGetTemplateDetailsQuery,
  useUpdateTemplateMutation,
} from "../../../../features/superAdmin/ticketService/template/template.api";
import {
  setTemplateData,
  setTemplateGeneralInfo,
  setTemplateRequiredFields,
  setTemplateRoles,
} from "../../../../features/superAdmin/ticketService/template/template.slice";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import {
  TemplateStepType,
  TemplateType,
} from "../../../../types/template.types";
import GeneralInfo from "./_components/GeneralInfo";
import RequiredFields from "./_components/RequiredFields";
import TemplateRoles from "./_components/TemplateRoles";
import TemplateStepper from "./_components/TemplateStepper";

const ManageTemplate = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const isUpdating = !!id;
  const basePath = "/admin/ticket-management/all-templates";
  const { open, handleOpen, handleClose } = useModal<unknown, "CONFIRM">();

  // Selectors
  const { template: templateData, user } = useAppSelector((state) => ({
    template: state.template?.template as TemplateType,
    user: state?.auth?.user,
  }));

  // Form Instance
  const [form] = Form.useForm();

  // Component States
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem("template-step");
    return savedStep ? parseInt(savedStep, 10) : 1;
  });

  // Mutations
  const [
    createTemplate,
    {
      data: createdData,
      isSuccess: created,
      isError: isCreateError,
      error: createError,
      isLoading: creating,
    },
  ] = useCreateTemplateMutation();

  const [
    updateTemplate,
    {
      isLoading: updating,
      isSuccess: updated,
      isError: isUpdatingError,
      error: updateError,
    },
  ] = useUpdateTemplateMutation();

  // Queries
  const {
    data: templateDetails,
    isError: isTempDetailsError,
    error: tempDetailsError,
  } = useGetTemplateDetailsQuery(id, {
    skip: !id,
  });

  // Handler Functions
  // Handle navigate previous step
  const handleNavigatePreviousStep = () => {
    setCurrentStep((step) => (step === 1 ? step : step - 1));
  };

  // Handle navigate next step
  const handleGoToNextPage = async () => {
    try {
      const values = await form.validateFields();
      localStorage.setItem(
        "template-form",
        JSON.stringify({ ...templateData, ...values }),
      );

      if (currentStep === 1) {
        dispatch(setTemplateGeneralInfo(values));
      } else if (currentStep === 2) {
        dispatch(setTemplateRequiredFields(values));
      } else if (currentStep === 3) {
        dispatch(setTemplateRoles(values));
      }

      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        handleOpen({ type: "CONFIRM" });
      }
    } catch (err) {}
  };

  const handleTemplateAction = () => {
    // Clear temporary data
    localStorage.removeItem("template-step");
    localStorage.removeItem("template-form");

    const sanitizeRequiredFields = templateData?.required_fields?.map(
      (item) => {
        const {
          question_text,
          is_required,
          required_field_type: type,
          options,
          settings,
        } = item || {};

        const content = {
          question_text,
          is_required,
          required_field_type: type,
          ...(type === "checkboxes" ||
          type === "multiple_choice" ||
          type === "dropdown"
            ? { options }
            : {}),
          ...(type === "file_upload" ? { settings } : {}),
        };
        return content;
      },
    );
    const sanitizeTemplateRoles = templateData?.ticket_roles
      ?.map((item) => {
        const hours = Number(item?.visibility_duration_hours) || 0;
        const minutes = Number(item?.visibility_duration_minutes) || 0;

        return {
          ...item,
          visibility_duration_minutes: hours * 60 + minutes,
          visibility_duration_hours: undefined,
        };
      })
      .map(({ visibility_duration_hours, ...rest }) => rest);

    const payload: TemplateType = {
      ...templateData,
      created_by: user?.id,
      required_fields: sanitizeRequiredFields,
      ticket_roles: sanitizeTemplateRoles,
    };

    if (id) {
      updateTemplate({ id, data: payload });
    } else {
      createTemplate(payload);
    }
  };

  // Effects
  useEffect(() => {
    localStorage.setItem("template-step", currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    const savedForm = localStorage.getItem("template-form");

    if (savedForm) {
      const parsedForm = JSON.parse(savedForm);
      dispatch(setTemplateData(parsedForm));
      form.setFieldsValue(parsedForm);
    }

    return () => {
      localStorage.removeItem("template-form");
      localStorage.removeItem("template-step");
    };
  }, [form, dispatch]);

  useEffect(() => {
    if (created) {
      handleClose();
      form.resetFields();
      handleOpen({ type: "VIEW" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created]);

  useEffect(() => {
    if (updated) {
      handleClose();
      form.resetFields();
      handleOpen({ type: "VIEW" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updated]);

  useEffect(() => {
    const templateData = templateDetails?.data;
    const templateRolesWithHoursAndMinutes = templateData?.ticketRoles?.map(
      (item) => {
        const totalMinutes = Number(item?.visibility_duration_minutes) || 0;
        const hours = Math.floor(totalMinutes / 60); // full hours
        const minutes = totalMinutes % 60; // remaining minutes

        return {
          ...item,
          visibility_duration_hours: hours,
          visibility_duration_minutes: minutes,
        };
      },
    );

    if (templateDetails) {
      form.setFieldsValue({
        ...templateData,
        required_fields: templateData?.requiredFields,
        ticket_roles: templateRolesWithHoursAndMinutes,
      });
    }
  }, [form, templateDetails]);

  // Errors
  useError(isCreateError, createError, "Template create fail.");
  useError(isUpdatingError, updateError, "Template update fail.");
  useError(isTempDetailsError, tempDetailsError, "Get template details fail.");

  const templateSteps: TemplateStepType[] = [
    {
      step: 1,
      label: "General Info",
      content: (
        <GeneralInfo
          templateKey={templateDetails?.data?.template_key}
          form={form}
        />
      ),
    },
    {
      step: 2,
      label: "Required Fields",
      content: <RequiredFields form={form} />,
    },
    {
      step: 3,
      label: "Workflow",
      content: <TemplateRoles form={form} />,
    },
  ];

  return (
    <SidebarLayout title="Ticket Management">
      <div className="flex items-center justify-between">
        <Button
          onClick={() => navigate(-1)}
          className="button-bordered-light h-10"
        >
          <BackButtonIcon />
          Back
        </Button>
        <div className="text-[#2D3643] text-2xl text-center my-4">
          {isUpdating ? "Update" : "Create"} New Template
        </div>
        <div className="w-[85px]"></div>
      </div>

      <div className="steps flex gap-4 justify-center mt-6">
        <TemplateStepper steps={templateSteps} currentStep={currentStep} />
      </div>

      <section className="max-w-5xl mx-auto mt-4">
        {/* Render Template Content */}
        {templateSteps.map((item) => item.step === currentStep && item.content)}

        {/* Render Navigation Buttons */}
        <div className="flex justify-between my-4">
          <Button
            disabled={currentStep === 1}
            className="!min-w-[105px] button-bordered-light h-10"
            onClick={handleNavigatePreviousStep}
          >
            Previous
          </Button>

          <Button
            className="min-w-[105px] h-10"
            type="primary"
            onClick={handleGoToNextPage}
          >
            Next
          </Button>
        </div>
      </section>

      {open.type === "VIEW" && (
        <ActionModal
          iconType="success"
          open={open?.open}
          loading={updating || creating}
          title={`Template ${isUpdating ? "Updated" : "Created"} Successfully`}
          subTitle={`The template has been ${
            isUpdating ? "updated" : "created"
          } successfully.`}
          cancelText="View Template"
          handleClose={handleClose}
          handleAction={() => navigate(basePath)}
          handleCancel={() => {
            const templateId = isUpdating ? id : createdData?.data?.id;
            navigate(`${basePath}/template-details/${templateId}`);
          }}
        />
      )}

      {open?.type === "CONFIRM" && (
        <ActionModal
          open={open?.open}
          loading={updating || creating}
          title={`Do you want to ${
            isUpdating ? "update" : "create"
          } the Template “${templateData?.name}”`}
          actionBtnText={`${isUpdating ? "Update" : "Create"}`}
          handleClose={handleClose}
          handleAction={handleTemplateAction}
          handleCancel={handleClose}
        />
      )}
    </SidebarLayout>
  );
};

export default ManageTemplate;
